/**
 * Suggested group combinations of one childcare (#1579 part 4).
 *
 * A non-modal sheet without a backdrop, so the planner stays visible while a suggestion
 * is previewed on its day. Nothing is stored here: "Create" hands the suggestion to the
 * combine groups dialog, and only saving that dialog creates a combination.
 */

import { useState } from 'react';
import { useLingui } from '@lingui/react';
import type { I18n } from '@lingui/core';
import { Eye, Lightbulb, Loader2, Plus, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { formatDateMedium } from '@/lib/format';
import { useCombinationSuggestions } from '@/hooks/useGroupCombinations';
import type { CombinationSuggestion } from '@/types/groupCombinations';
import { parseDateString } from '@/utils/dateUtils';
import { combinationName } from './combinationLayout';

export interface SuggestionChildcare {
  id?: string;
  name?: string;
}

export interface CombinationSuggestionsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  childcares: SuggestionChildcare[];
  /** The planner's childcare filter. Without one the panel shows a childcare select. */
  filterChildcareId: string | null;
  /** The suggestion previewed in the planner; its row is highlighted. */
  preview?: CombinationSuggestion | null;
  /** Opens the combine groups dialog filled in. from is the first day of the period. */
  onCreate: (suggestion: CombinationSuggestion, from: string) => void;
  /** Shows the suggestion on its first day in the planner. */
  onShowOnDay: (suggestion: CombinationSuggestion, from: string) => void;
}

/** Identifies a suggestion within one response. */
export const suggestionKey = (s: CombinationSuggestion): string =>
  [s.host_group_id, s.member_group_ids.join(','), s.weekdays.join(','), s.start, s.end].join('|');

/** "Mo, We" from ISO weekdays, with the planner's short weekday names. */
export function formatWeekdays(i18n: I18n, weekdays: number[]): string {
  const names: Record<number, string> = {
    1: i18n._({ id: 'weekday.mon.short', message: 'Mo' }),
    2: i18n._({ id: 'weekday.tue.short', message: 'Tu' }),
    3: i18n._({ id: 'weekday.wed.short', message: 'We' }),
    4: i18n._({ id: 'weekday.thu.short', message: 'Th' }),
    5: i18n._({ id: 'weekday.fri.short', message: 'Fr' }),
    6: i18n._({ id: 'weekday.sat.short', message: 'Sa' }),
    7: i18n._({ id: 'weekday.sun.short', message: 'Su' }),
  };
  return weekdays.map((day) => names[day] ?? String(day)).join(', ');
}

export function CombinationSuggestionsPanel({
  open,
  onOpenChange,
  childcares,
  filterChildcareId,
  preview,
  onCreate,
  onShowOnDay,
}: CombinationSuggestionsPanelProps) {
  const { i18n } = useLingui();
  const [pickedChildcareId, setPickedChildcareId] = useState<string | null>(null);
  const childcareId = filterChildcareId ?? pickedChildcareId;
  const { data, isLoading, isError, refetch } = useCombinationSuggestions(childcareId, undefined, {
    enabled: open,
  });

  const previewKey = preview ? suggestionKey(preview) : null;
  const namedChildcares = childcares.filter(
    (c): c is SuggestionChildcare & { id: string } => !!c.id
  );
  const filterChildcareName = childcares.find((c) => c.id === filterChildcareId)?.name;
  const fromDate = parseDateString(data?.from);

  const renderList = () => {
    if (!childcareId) {
      return (
        <p className="text-sm text-muted-foreground" data-testid="combination-suggestions-pick">
          {i18n._({
            id: 'shifts.suggestions.pickChildcare',
            message: 'Choose a childcare to see its suggestions.',
          })}
        </p>
      );
    }
    if (isLoading) {
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          {i18n._({
            id: 'shifts.suggestions.loading',
            message: 'Looking for combinations that save staff...',
          })}
        </div>
      );
    }
    if (isError || !data) {
      return (
        <div className="space-y-2" role="alert">
          <p className="text-sm text-destructive">
            {i18n._({
              id: 'shifts.suggestions.error',
              message: 'The suggestions could not be loaded.',
            })}
          </p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            {i18n._({ id: 'common.retry', message: 'Try again' })}
          </Button>
        </div>
      );
    }
    if (data.suggestions.length === 0) {
      return (
        <p className="text-sm text-muted-foreground" data-testid="combination-suggestions-empty">
          {i18n._({
            id: 'shifts.suggestions.empty',
            message: 'No combination saves staff in these 4 weeks.',
          })}
        </p>
      );
    }
    return (
      <ul className="space-y-3">
        {data.suggestions.map((suggestion) => {
          const key = suggestionKey(suggestion);
          const previewed = key === previewKey;
          return (
            <li
              key={key}
              data-testid="combination-suggestion"
              className={cn('space-y-2 rounded-md border p-3', previewed && 'border-info-on-surface/60 bg-info-muted')}
            >
              <div className="flex items-center gap-1 font-medium">
                <Users className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span>{combinationName(suggestion.host_group_name, suggestion.member_group_names)}</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {i18n._({
                  id: 'shifts.suggestions.window',
                  message: '{weekdays} {start}-{end}',
                  values: {
                    weekdays: formatWeekdays(i18n, suggestion.weekdays),
                    start: suggestion.start,
                    end: suggestion.end,
                  },
                })}
              </p>
              <p className="text-sm">
                <span className="font-medium text-primary">
                  {i18n._({
                    id: 'shifts.combination.block.saves',
                    message: '{count, plural, one {saves # staff} other {saves # staff}}',
                    values: { count: suggestion.staff_saved },
                  })}
                </span>{' '}
                <span className="text-muted-foreground">
                  {i18n._({
                    id: 'shifts.suggestions.weeks',
                    message: '({matched} of {total} weeks)',
                    values: { matched: suggestion.weeks_matched, total: data.weeks },
                  })}
                </span>
              </p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => onCreate(suggestion, data.from)}>
                  <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
                  {i18n._({ id: 'shifts.suggestions.create', message: 'Create' })}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  aria-pressed={previewed}
                  onClick={() => onShowOnDay(suggestion, data.from)}
                >
                  <Eye className="mr-1 h-4 w-4" aria-hidden="true" />
                  {i18n._({ id: 'shifts.suggestions.showOnDay', message: 'Show on day' })}
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        hideOverlay
        closeLabel={i18n._({ id: 'common.close', message: 'Close' })}
        className="flex w-full flex-col gap-4 overflow-y-auto sm:max-w-md"
        // The planner stays usable behind the panel; only the close button and Escape close it.
        onInteractOutside={(event) => event.preventDefault()}
        data-testid="combination-suggestions-panel"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5" aria-hidden="true" />
            {i18n._({ id: 'shifts.suggestions.title', message: 'Suggested combinations' })}
          </SheetTitle>
          <SheetDescription>
            {i18n._({
              id: 'shifts.suggestions.explained',
              message:
                'A combination lets two or three groups share one room and their staff for a time window, for example early in the morning when few children are there. These windows would need fewer staff on at least 3 of the next 4 weeks, judged by the children\'s booked times and their usual drop-off and pick-up times.',
            })}{' '}
            {i18n._({
              id: 'shifts.suggestions.nothingChanges',
              message: 'Nothing changes until you save a combination.',
            })}
          </SheetDescription>
        </SheetHeader>

        {filterChildcareId ? (
          filterChildcareName && (
            <p className="text-sm text-muted-foreground">
              {i18n._({ id: 'shifts.filter.childcare', message: 'Childcare' })}: {filterChildcareName}
            </p>
          )
        ) : (
          <div className="space-y-2">
            <label id="combination-suggestions-childcare-label" className="text-sm text-muted-foreground">
              {i18n._({ id: 'shifts.filter.childcare', message: 'Childcare' })}
            </label>
            <Select value={pickedChildcareId ?? undefined} onValueChange={setPickedChildcareId}>
              <SelectTrigger
                aria-labelledby="combination-suggestions-childcare-label"
                data-testid="combination-suggestions-childcare"
              >
                <SelectValue
                  placeholder={i18n._({
                    id: 'shifts.suggestions.childcarePlaceholder',
                    message: 'Choose a childcare',
                  })}
                />
              </SelectTrigger>
              <SelectContent>
                {namedChildcares.map((childcare) => (
                  <SelectItem key={childcare.id} value={childcare.id}>
                    {childcare.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {fromDate && childcareId && (
          <p className="text-sm text-muted-foreground" data-testid="combination-suggestions-period">
            {i18n._({
              id: 'shifts.suggestions.period',
              message: '4 weeks from {date}',
              values: { date: formatDateMedium(fromDate, i18n.locale) },
            })}
          </p>
        )}

        {renderList()}
      </SheetContent>
    </Sheet>
  );
}
